import { useEffect } from 'react';

export const useSEO = ({
  title,
  description,
  keywords,
  ogImage,
  canonical,
  robots = 'index, follow',
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  siteName = 'TrendPhile'
}) => {
  useEffect(() => {
    // Clear existing meta tags
    const existingMetas = document.querySelectorAll('meta[data-seo="true"]');
    existingMetas.forEach(meta => meta.remove());

    // Set document title
    document.title = title;

    const metaTags = [
      // Standard Meta Tags
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'author', content: author || 'TrendPhile Team' },
      { name: 'robots', content: robots },

      // Open Graph (Facebook, LinkedIn, Discord, Reddit)
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: ogImage },
      { property: 'og:url', content: canonical },
      { property: 'og:type', content: type },
      { property: 'og:site_name', content: siteName },
      { property: 'og:locale', content: 'en_US' },

      // Twitter Cards
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:site', content: '@TrendPhile' },
      { name: 'twitter:creator', content: '@TrendPhile' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: ogImage },

      // Discord-specific enhancements
      { name: 'theme-color', content: '#6366f1' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:image:alt', content: `${title} - TrendPhile` },

      // Reddit-specific enhancements  
      { property: 'og:image:type', content: 'image/jpeg' },
      { property: 'og:updated_time', content: new Date().toISOString() },

      // Article-specific (for Rich Results)
      ...(type === 'article' ? [
        { property: 'article:author', content: author || 'TrendPhile Team' },
        { property: 'article:published_time', content: publishedTime },
        { property: 'article:modified_time', content: modifiedTime },
        { property: 'article:section', content: 'Trend Analysis' },
        { property: 'article:tag', content: keywords }
      ] : []),

      // Schema.org for Rich Results
      { name: 'application-name', content: 'TrendPhile' },
      { name: 'apple-mobile-web-app-title', content: 'TrendPhile' },
      { name: 'msapplication-TileColor', content: '#6366f1' }
    ];

    // Add meta tags to head
    metaTags.forEach(({ name, property, content }) => {
      if (content) {
        const meta = document.createElement('meta');
        if (name) meta.setAttribute('name', name);
        if (property) meta.setAttribute('property', property);
        meta.setAttribute('content', content);
        meta.setAttribute('data-seo', 'true');
        document.head.appendChild(meta);
      }
    });

    // Add canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.href = canonical;
    } else {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      canonicalLink.href = canonical;
      document.head.appendChild(canonicalLink);
    }

    // Cleanup function
    return () => {
      const seoMetas = document.querySelectorAll('meta[data-seo="true"]');
      seoMetas.forEach(meta => meta.remove());
    };
  }, [title, description, keywords, ogImage, canonical, robots, type, author, publishedTime, modifiedTime, siteName]);
};
